import { AlertTriangle, List, Trash, Users } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetParticipantFieldImportsForEventQuery as GetParticipantFieldImportsForEvent,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../common/helpers';
import { fieldTypeIcons } from '../../Events/Event/Fields/ParticipantFieldForm';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import DeleteParticipantFieldImportMutation from './DeleteParticipantFieldImportMutation';
import FinalizeParticipantFieldImportForm from './FinalizeParticipantFieldImportForm';
import GetParticipantFieldImportDetailsQuery from './GetParticipantFieldImportDetailsQuery';
import GetParticipantFieldImportsForEventQuery from './GetParticipantFieldImportsForEventQuery';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useUrlState from '../../../common/useUrlState';

type ParticipantFieldImport = GetParticipantFieldImportsForEvent['project']['event']['participant_field_imports'][0];

export interface ParticipantFieldImportCardProps {
  participantFieldImport: ParticipantFieldImport;
  active?: boolean;
  muted?: boolean;
  onDelete?: () => void;
  refetchQueries?: DocumentNode[];
}

const ParticipantFieldImportCard = ({
  participantFieldImport, active, muted, onDelete, refetchQueries = [GetParticipantFieldImportsForEventQuery],
}: ParticipantFieldImportCardProps) => {
  const project = useProject();

  // Remember any params in the URL, such as the current page
  const [urlParams] = useUrlState({
    parse: (urlParams) => urlParams,
  });
  const queryParams = encodeUrl(urlParams);

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/participants/imports${!active ? `/${participantFieldImport.id}` : ''}?${queryParams}`}
      open={active}
      muted={muted}
      preview={(
        <ParticipantFieldImportPreview
          participantFieldImport={participantFieldImport}
          onDelete={onDelete}
          refetchQueries={refetchQueries}
        />
      )}
      body={<ParticipantFieldImportBody participantFieldImport={participantFieldImport} />}
      id={`ParticipantFieldImportCard_${participantFieldImport.id}`}
      ariaLabel={participantFieldImport.filename}
      key={participantFieldImport.id}
    />
  );
};

interface ParticipantFieldImportPreviewProps {
  participantFieldImport: ParticipantFieldImport;
  onDelete?: () => void;
  refetchQueries?: DocumentNode[];
}

const ParticipantFieldImportPreview = ({
  participantFieldImport, onDelete, refetchQueries = [],
}: ParticipantFieldImportPreviewProps) => {
  const { t } = useTranslation();
  const { parseDate, formatDate, formatNumber } = useLocale();
  const notifier = useNotifier();

  const [
    deleteImport, { loading: deleting },
  ] = useMutation(
    DeleteParticipantFieldImportMutation,
    {
      variables: {
        id: participantFieldImport.id,
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.import_deleted'));
        onDelete?.();
      },
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: participantFieldImport.filename }),
    body: t('delete_import_description'),
    onConfirm: () => deleteImport(),
  }, [participantFieldImport]);

  const skippedCount = participantFieldImport.errors_count;

  return (
    <ContainerWithOptions
      renderOptions={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!participantFieldImport.deletable || deleting}
        />
      )}
    >
      <UI.GridContainer
        sc={{ columns: ['3fr 1fr', '6fr 2fr 5fr 2fr 3fr'], alignVertical: 'center', deleting, gutter: 0.5 }}
      >
        <UI.Div style={{ minWidth: 0 }} sc={{ column: ['span 2', 'auto'] }}>
          <UI.H4>
            <UI.Truncate>
              {participantFieldImport.filename}
            </UI.Truncate>
          </UI.H4>
        </UI.Div>
        <UI.Div sc={{ color: 'gray.500' }}>
          <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
            <UI.Icon>
              <List />
            </UI.Icon>
            {' '}
            {formatNumber(participantFieldImport.length)}
            <UI.Tooltip sc={{ alignH: 'center' }}>
              {t('rows')}
            </UI.Tooltip>
          </UI.TooltipContainer>

        </UI.Div>
        <UI.Div sc={{ color: 'gray.500' }}>
          <UI.Icon sc={{ fontSize: 2 }}>
            {participantFieldImport.field && fieldTypeIcons[participantFieldImport.field.type]}
            {!participantFieldImport.field && <Users />}
          </UI.Icon>
          {' '}
          {participantFieldImport.field && participantFieldImport.field.title}
          {!participantFieldImport.field && t('participants')}
        </UI.Div>
        <UI.Div sc={{ color: 'gray.500' }}>
          {formatDate(
            parseDate(participantFieldImport.created_at, { timezone: 'UTC' }),
            { format: 'display_compact_date_time' },
          )}
        </UI.Div>
        <UI.Div sc={{ textAlign: ['left', 'right'], small: true }}>
          {!participantFieldImport.validated_at && (
            <UI.Label sc={{ fontWeight: 500, background: 'gray.100', color: 'gray.700' }}>
              <UI.Loader sc={{ size: 12, mb: 0.5, brand: 'gray.700' }} />
              {' '}
              {t('validating')}
            </UI.Label>
          )}
          {' '}
          {participantFieldImport.validated_at && skippedCount === 0 && participantFieldImport.ready_to_import && (
            <UI.Label sc={{ fontWeight: 500, background: 'secondary.50', color: 'secondary.700' }}>
              {t('validated')}
            </UI.Label>
          )}
          {' '}
          {participantFieldImport.validated_at && skippedCount > 0 && participantFieldImport.ready_to_import && (
            <UI.Label sc={{ fontWeight: 500, background: 'info.100', color: 'info.700' }}>
              <UI.Icon>
                <AlertTriangle />
              </UI.Icon>
              {' '}
              {t('n_rows_invalid', {
                count: skippedCount,
                value: formatNumber(skippedCount),
              })}
            </UI.Label>
          )}
          {participantFieldImport.validated_at && !participantFieldImport.ready_to_import
            && !participantFieldImport.importing && !participantFieldImport.imported_at && (
            <UI.Label sc={{ fontWeight: 500, background: 'error.100', color: 'error.700' }}>
              {t('rejected')}
            </UI.Label>
          )}
          {' '}
          {participantFieldImport.importing && (
            <UI.Label sc={{ fontWeight: 500, background: 'gray.100', color: 'gray.700' }}>
              <UI.Loader sc={{ size: 12, mb: 0.5, brand: 'gray.700' }} />
              {' '}
              {t('importing')}
            </UI.Label>
          )}
          {' '}
          {participantFieldImport.imported_at && (
            <UI.Label sc={{ fontWeight: 500, background: 'primary.25', color: 'primary.600' }}>
              {t('imported')}
            </UI.Label>
          )}
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

interface ParticipantFieldImportBodyProps {
  participantFieldImport: ParticipantFieldImport;
}

const ParticipantFieldImportBody = ({ participantFieldImport }: ParticipantFieldImportBodyProps) => {
  const project = useProject();

  const { data, refetch } = useQuery(
    GetParticipantFieldImportDetailsQuery,
    {
      variables: {
        project_id: project.id,
        participant_field_import_id: participantFieldImport.id,
      },
    },
  );

  useEffect(() => {
    if (participantFieldImport) {
      refetch();
    }
  }, [participantFieldImport, refetch]);

  if (!data) {
    return <UI.PageLoader height={114} /* Height of the 'import is validating' message */ />;
  }

  return (
    <FinalizeParticipantFieldImportForm
      participantFieldImport={data.project.participant_field_import}
      defaultExportColumns={data.project.default_export_columns}
    />
  );
};

export default ParticipantFieldImportCard;
